import { ChevronDown, X } from "lucide-react";
import React from "react";
import ReactDOM from "react-dom";

function DropDownWithSearch({
  dropdownData = [],
  onSelect = () => {},
  placeholder = "Select an option",
  selectedValue = null,
  style = {},
  className = "",
  specialClasses="",
  handleCrossClick=()=>{},
  showClearButton = true,
  disableSort = false,
  onlyWholeNumbers = false,
  maxLength = null,
  maxNumericValue = undefined,
  onInputChange = null,
}) {
  const [isOpen, setIsOpen] = React.useState(false);
  const [inputValue, setInputValue] = React.useState(
    selectedValue?.label || ""
  );
  const [filteredData, setFilteredData] = React.useState(dropdownData);
  const [highlightedIndex, setHighlightedIndex] = React.useState(-1);

  const wrapperRef = React.useRef(null);
  const dropdownRef = React.useRef(null);

  const handleClear = (e) => {
    e.stopPropagation();
    setInputValue("");
    setIsOpen(true);
    handleSelect({ label: "", value: "all" });
    handleCrossClick();
  };

  const handleKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((prev) =>
        prev < filteredData.length - 1 ? prev + 1 : 0
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((prev) =>
        prev > 0 ? prev - 1 : filteredData.length - 1
      );
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (highlightedIndex >= 0 && highlightedIndex < filteredData.length) {
        handleSelect(filteredData[highlightedIndex]);
      } else if (inputValue.trim() !== "") {
        handleSelect({ label: inputValue, value: inputValue });
      }
    }
  };

  // Filter data based on input
  React.useEffect(() => {
    if (!inputValue?.trim()) {
      setFilteredData(dropdownData);
      return;
    }

    const search = inputValue.toLowerCase().trim();

    // If disableSort is true and the input matches the selected value,
    // show all options in their original order. This allows the user to see the
    // full sequence when they first open a pre-filled dropdown.
    if (disableSort && inputValue === selectedValue?.label) {
      setFilteredData(dropdownData);
      return;
    }

    const matching = [];
    const nonMatching = [];

    dropdownData.forEach((item) => {
      if (item.label.toLowerCase().includes(search)) {
        matching.push(item);
      } else {
        nonMatching.push(item);
      }
    });

    if (disableSort) {
      setFilteredData(matching);
    } else {
      setFilteredData([...matching, ...nonMatching]);
    }
  }, [inputValue, dropdownData, disableSort, selectedValue]);

  // Close dropdown on outside click, but ignore portal clicks
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target) &&
        !dropdownRef.current?.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Handle item selection
  const handleSelect = (item) => {
    setInputValue(item.label);
    console.log({ item });
    onSelect(item);
    setIsOpen(false);
  };

  // Sync internal input value with selectedValue prop when it changes in parent
  React.useEffect(() => {
    setInputValue(selectedValue?.label || "");
  }, [selectedValue]);



  // Render portal dropdown

  return (
    <div
      ref={wrapperRef}
      className={`relative ${className}`}
      style={{ ...style }}
      onMouseDown={(e) => e.stopPropagation()} 
    >
      <div className="relative w-full">
        <input
          type="text"
          placeholder={placeholder}
          value={inputValue}
          onFocus={() => setIsOpen(true)}
          onChange={(e) => {
            let val = e.target.value;
            if (onlyWholeNumbers) {
              val = val.replace(/\D/g, "");
            }
            if (maxLength !== null && val.length > maxLength) {
              return;
            }
            if (maxNumericValue !== undefined && val !== "") {
              const numVal = parseInt(val, 10);
              if (!isNaN(numVal) && numVal > maxNumericValue) {
                return;
              }
            }
            setInputValue(val);
            setIsOpen(true);
            if (onInputChange) onInputChange(val);
          }}
          className={`px-4 py-2 pr-16 w-full border border-gray-300 rounded-[30px] 
            bg-bgPrimary text-textPrimary focus:border-gray-400 focus:outline-none
            dark:glass-effect dark:bg-[linear-gradient(233.57deg,rgba(34,52,97,0.25)_-5.01%,rgba(11,10,31,0.05)_100%)] dark:border-[rgba(229,229,229,0.5)] dark:text-[rgba(216,216,216,1)] dark:focus:border-white overflow-hidden
              text-ellipsis truncate placeholder:text-sm placeholder:text-gray-400 dark:placeholder:text-[rgba(216,216,216,0.6)] ${specialClasses}`}
          onKeyDown={handleKeyDown}
        />
        <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2">
          {showClearButton && (
            inputValue && (
              <X
                size={16}
                className="dark:text-gray-400 text-textSecondary dark:hover:text-white hover:text-gray-400 cursor-pointer transition-colors"
                onClick={handleClear}
              />
            )
          )}
          <ChevronDown
            size={16}
            className={`dark:text-gray-400 text-textSecondary dark:hover:text-white hover:text-gray-300 cursor-pointer transition-all  duration-200 ${
              isOpen ? "rotate-180" : "rotate-0"
            }`}
          onClick={()=>{setIsOpen(!isOpen)}}
          />
        </div>
      </div>
      {isOpen && (
        <div
          ref={dropdownRef}
          className="absolute left-0 right-0 mt-2 shadow-lg rounded-xl overflow-auto z-[9999] max-h-[200px] dark:bg-[#0a1733] bg-bgSecondary border border-[rgba(229,229,229,0.5)]"
         
        >
          {filteredData.length > 0 ? (
            filteredData.map((item, index) => (
              <div
                key={item.value}
                className={`px-4 py-2 cursor-pointer dark:text-[#D9D9D9] text-textSecondary ${
                  index === highlightedIndex
                    ? "bg-[rgba(229,229,229,0.2)]"
                    : "hover:bg-[rgba(229,229,229,0.1)]"
                }`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleSelect(item);
                }}
              >
                {item.label}
              </div>
            ))
          ) : (
            <div
              className="px-4 py-2 dark:text-[rgba(216,216,216,0.6)] text-textSecondary italic"
            >
              No results found
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default DropDownWithSearch;
